import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import {
  marcarCrmPerdido, obterCrmHistoricoPerdas, reabrirCrmPerdido,
  parseApiError, type CrmLoss, type CrmLossHistory, type CrmLossStage, type CrmStage,
} from '../../services/api.ts';

import { getLossReasons, type ReasonCatalog } from '../../services/apiCrmReasons.ts';

export const CRM_STAGE_LABELS: Record<CrmStage, string> = {
  leads: 'Lead', appointments: 'Agendamento', attendances: 'Comparecimento', noShows: 'No-show', sales: 'Venda',
};
type LossContact = { id?: number; lead_id?: number; name?: string; nome?: string; phone?: string };
type Target = { leadId: number; name: string; stage?: CrmLossStage; loss?: CrmLoss };
type Actions = {
  enabled: boolean;
  mark: (contact: LossContact, stage: CrmLossStage) => void;
  history: (contact: LossContact) => void;
  reopen: (loss: CrmLoss) => void;
};
const LossContext = createContext<Actions>({ enabled: false, mark: () => {}, history: () => {}, reopen: () => {} });
export const useCrmLossActions = () => useContext(LossContext);
const targetFor = (contact: LossContact): Target => ({ leadId: contact.lead_id ?? contact.id!, name: contact.name || contact.nome || 'Contato' });

export const CrmLossProvider: React.FC<{
  enabled: boolean;
  getAuth: () => { effectiveClientId: number; clinicId: number };
  onChanged: (stage: CrmStage | 'lost') => Promise<void>;
  formatDate: (date?: string) => string;
  Modal: React.ComponentType<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }>;
  children: React.ReactNode;
}> = ({ enabled, getAuth, onChanged, formatDate, Modal, children }) => {
  const { isDark } = useTheme();
  const [target, setTarget] = useState<Target | null>(null);
  const [mode, setMode] = useState<'mark' | 'history' | 'reopen'>('mark');
  const [reason, setReason] = useState('');
  const [catalog, setCatalog] = useState<ReasonCatalog | null>(null);
  const selectedReason = catalog?.items.find(item => String(item.id) === reason);
  const [observation, setObservation] = useState('');
  const [justification, setJustification] = useState('');
  const [history, setHistory] = useState<CrmLossHistory | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadHistory, setReloadHistory] = useState(0);
  const requestRef = useRef(0);
  const savingRef = useRef(false);
  const inputClass = `mt-1 w-full rounded-xl border p-3 text-sm focus:ring-2 focus:ring-sminy-primary ${isDark ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-slate-300 bg-white text-slate-900'}`;
  const open = (next: Target, nextMode: typeof mode) => {
    requestRef.current += 1;
    setLoading(false); setTarget(next); setMode(nextMode); setCatalog(null); setReason(''); setObservation(''); setJustification(''); setHistory(null); setError('');
  };
  useEffect(() => {
    if (!target || mode !== 'mark') return;
    const request = ++requestRef.current;
    setLoading(true);
    const { effectiveClientId, clinicId } = getAuth();
    getLossReasons(effectiveClientId, clinicId, true)
      .then(result => { if (request === requestRef.current) setCatalog(result); })
      .catch(err => { if (request === requestRef.current) setError(parseApiError(err, 'Não foi possível carregar os motivos.').message); })
      .finally(() => { if (request === requestRef.current) setLoading(false); });
    return () => { requestRef.current += 1; };
  }, [target, mode, getAuth, reloadHistory]);
  useEffect(() => {
    if (!target || mode === 'mark') return;
    const request = ++requestRef.current;
    setLoading(true);
    const { effectiveClientId, clinicId } = getAuth();
    obterCrmHistoricoPerdas(effectiveClientId, clinicId, target.leadId)
      .then(result => { if (request === requestRef.current) setHistory(result); })
      .catch(err => { if (request === requestRef.current) setError(parseApiError(err, 'Não foi possível carregar o histórico.').message); })
      .finally(() => { if (request === requestRef.current) setLoading(false); });
    return () => { requestRef.current += 1; };
  }, [target, mode, getAuth, reloadHistory]);
  const close = () => { if (!savingRef.current) { setTarget(null); setError(''); } };
  const submit = async () => {
    if (!target || savingRef.current) return;
    if (mode === 'mark' && (!selectedReason || (selectedReason.requires_observation && !observation.trim()))) {
      setError('Selecione um motivo e preencha a observação quando obrigatória.'); return;
    }
    if (mode === 'reopen' && (!justification.trim() || !history?.reopen_stage || !target.loss)) {
      setError('Informe a justificativa e aguarde a etapa de destino.'); return;
    }
    savingRef.current = true; setBusy(true); setError('');
    try {
      const { effectiveClientId, clinicId } = getAuth();
      const result = mode === 'mark'
        ? await marcarCrmPerdido(effectiveClientId, clinicId, target.leadId, { expected_stage: target.stage!, reason: selectedReason!.name, ...(catalog?.legacy ? {} : { reason_id: selectedReason!.id }), observation: observation.trim() || undefined })
        : await reabrirCrmPerdido(effectiveClientId, clinicId, target.leadId, { loss_id: target.loss!.id, expected_stage: history!.reopen_stage!, justification: justification.trim() });
      await onChanged(result.stage);
      setTarget(null);
    } catch (err) {
      setError(parseApiError(err, 'Não foi possível salvar. Atualize os dados e tente novamente.').message);
    } finally { savingRef.current = false; setBusy(false); }
  };
  return <LossContext.Provider value={{ enabled, mark: (contact, stage) => open({ ...targetFor(contact), stage }, 'mark'), history: contact => open(targetFor(contact), 'history'), reopen: loss => open({ ...targetFor(loss), loss }, 'reopen') }}>
    {children}
    <Modal isOpen={enabled && !!target} onClose={close} title={mode === 'mark' ? 'Marcar como perdido' : mode === 'reopen' ? 'Reabrir contato' : 'Histórico de perdas'}>
      <div className={`space-y-4 text-sm ${isDark ? 'text-gray-200' : 'text-slate-700'}`}>
        <p className="font-semibold break-words">{target?.name}</p>
        {mode === 'mark' && <>
          <p>O contato sairá de <strong>{target?.stage && CRM_STAGE_LABELS[target.stage]}</strong> e ficará em Perdidos até uma reabertura manual. O histórico será preservado. Follow-ups comerciais automáticos ficarão pausados; consultas e confirmações de agenda serão mantidas.</p>
          <label className="block">Motivo da perda <span aria-hidden="true">*</span>
            <select aria-label="Motivo da perda" className={inputClass} value={reason} disabled={busy || loading} onChange={e => setReason(e.target.value)}>
              <option value="">Selecione um motivo</option>
              {catalog?.items.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          {catalog?.can_manage && <a href="/configuracoes/crm" className="inline-block text-sminy-primary underline">Gerenciar motivos</a>}
          {catalog && catalog.items.length === 0 && <p role="status">Nenhum motivo cadastrado. {catalog.can_manage ? 'Cadastre um motivo para continuar.' : 'Solicite à conta principal o cadastro de um motivo.'}</p>}
          <label className="block">Observação {selectedReason?.requires_observation ? '(obrigatória)' : '(opcional)'}
            <textarea aria-label="Observação da perda" maxLength={2000} rows={3} className={inputClass} value={observation} disabled={busy} onChange={e => setObservation(e.target.value)} />
          </label>
        </>}
        {loading && <p role="status">{mode === 'mark' ? 'Carregando motivos...' : 'Carregando histórico e etapa de destino...'}</p>}
        {history?.reopen_error && <p role="alert" className={isDark ? 'text-red-300' : 'text-red-700'}>{history.reopen_error}</p>}
        {mode === 'reopen' && history?.reopen_stage && <>
          <p>O contato voltará para <strong>{CRM_STAGE_LABELS[history.reopen_stage]}</strong>, conforme os registros atuais.</p>
          <label className="block">Justificativa da reabertura
            <textarea aria-label="Justificativa da reabertura" maxLength={2000} rows={3} className={inputClass} value={justification} disabled={busy} onChange={e => setJustification(e.target.value)} />
          </label>
        </>}
        {mode === 'history' && history && <div className="space-y-3">
          {history.items.length === 0 && <p>Nenhuma perda registrada para este contato.</p>}
          {history.items.map(item => <article key={item.id} className={`rounded-xl border p-3 space-y-1 ${isDark ? 'border-gray-600' : 'border-slate-200'}`}>
            <p className="font-medium">{CRM_STAGE_LABELS[item.from_stage]} → Perdido</p>
            <p>{formatDate(item.lost_at)} · {item.actor_name}</p>
            <p>Motivo: {item.reason}</p>
            {item.observation && <p className="whitespace-pre-wrap break-words">{item.observation}</p>}
            {item.reopened_at && <div className="border-t pt-2 mt-2">
              <p>Reaberto {item.reopened_stage ? `para ${CRM_STAGE_LABELS[item.reopened_stage]}` : ''} em {formatDate(item.reopened_at)}{item.reopened_by_name ? ` · ${item.reopened_by_name}` : ''}</p>
              {item.reopen_justification && <p className="whitespace-pre-wrap break-words">{item.reopen_justification}</p>}
            </div>}
          </article>)}
        </div>}
        {error && <div className="space-y-2"><p role="alert" className={isDark ? 'text-red-300' : 'text-red-700'}>{error}</p>
          {<button type="button" disabled={busy || loading} className="text-sminy-primary underline" onClick={() => { setError(''); setHistory(null); setReloadHistory(value => value + 1); }}>{mode === 'mark' ? 'Atualizar motivos' : 'Atualizar histórico e destino'}</button>}
        </div>}
        <div className="flex flex-wrap justify-end gap-2 pb-[env(safe-area-inset-bottom)]">
          <button type="button" disabled={busy} onClick={close} className={`rounded-xl border px-4 py-2 ${isDark ? 'border-gray-600' : 'border-slate-300'}`}>{mode === 'history' ? 'Fechar' : 'Cancelar'}</button>
          {mode !== 'history' && <button type="button" onClick={submit} disabled={busy || loading || (mode === 'mark' && !catalog?.items.length) || (mode === 'reopen' && !history?.reopen_stage)} className="rounded-xl bg-sminy-primary text-white px-4 py-2 disabled:opacity-50">{busy ? 'Salvando...' : mode === 'mark' ? 'Confirmar perda' : 'Confirmar reabertura'}</button>}
        </div>
      </div>
    </Modal>
  </LossContext.Provider>;
};
